import { useEffect, useState } from 'react';

function queryMatches(query: string): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(query).matches;
}

/**
 * Subscribes to a CSS media query and returns whether it currently matches.
 * Resolved during the first render, so callers never see a frame with the
 * wrong answer.
 *
 * @example
 * const isDesktop = useMediaQuery('(min-width: 768px)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => queryMatches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mql = window.matchMedia(query);
    const update = () => setMatches(mql.matches);

    // The query may have changed since the state was initialised.
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [query]);

  return matches;
}

/** Tailwind's `md` breakpoint (768px). Anything below is treated as mobile. */
export const MOBILE_BREAKPOINT_QUERY = '(max-width: 767px)';

/**
 * Convenience hook: true on viewports narrower than Tailwind's `md` breakpoint.
 * Use to compose responsive filter field sets, e.g.
 * `const fields = isMobile ? base.filter(...) : base;`
 */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_BREAKPOINT_QUERY);
}
